import { useMemo } from 'react'

import { F0Select } from '@factorialco/f0-react'
import {
  TicketingTeamsSelectorQuery,
  TicketingTeamsSelectorDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

export interface Team {
  id: string
  name: string
}

interface TeamSelectorProps {
  value?: Team | null
  onSelect: (team: Team | null) => void
  label?: string
  hideLabel?: boolean
  emptyLabel?: string
  mandatory?: boolean
  disabled?: boolean
  error?: boolean
}

export const TeamSelector = ({
  value,
  onSelect,
  label,
  hideLabel = false,
  emptyLabel,
  mandatory = false,
  disabled = false,
  error = false,
}: TeamSelectorProps) => {
  const { data: teamsData } = useQuery<TicketingTeamsSelectorQuery, Record<string, never>>(
    TicketingTeamsSelectorDocument
  )

  const options = useMemo(
    () =>
      (teamsData?.teams?.teamsConnection?.edges || [])
        .filter((edge) => Boolean(edge?.node?.id && edge?.node?.name))
        .map((edge) => ({
          label: edge.node?.name || '',
          value: String(edge.node?.id || ''),
          avatar: {
            type: 'team' as const,
            name: edge.node?.name || '',
            src: '',
          },
        })),
    [teamsData]
  )

  const handleChange = (teamId: string) => {
    const edge = teamsData?.teams?.teamsConnection?.edges?.find(
      (e) => String(e.node?.id) === teamId
    )
    onSelect(
      edge?.node
        ? {
            id: String(edge.node.id),
            name: edge.node.name || '',
          }
        : null
    )
  }

  return (
    <F0Select
      label={label}
      hideLabel={hideLabel}
      value={value?.id}
      onChange={handleChange}
      options={options}
      placeholder={emptyLabel}
      size="sm"
      searchable
      showSearchBox
      disabled={disabled}
      error={error}
      required={mandatory}
    />
  )
}
